import React, { useCallback, useState } from 'react';

import { useSnackbar } from 'notistack';

import { Box, Button, Container, Stack, Tab, Tabs } from '@mui/material';

import PageHeader from '~/components/Common/Elements/PageHeader';

import { ROLE_HERO_PLANNER_ADMIN } from '~/consts/roles';

import {
  useRefreshTranslationOverview,
  useTranslateTranslationOverview,
  useUploadPendingTranslationOverview,
  useUploadTranslationOverviewLocale,
} from '~/hooks/apis/customer-communication/useTranslationOverview';
import useCurrentUser from '~/hooks/useCurrentUser';

import CatalogsTab from './CatalogsTab';
import ConfirmActionDialog from './ConfirmActionDialog';
import LocalesTab from './LocalesTab';
import { MUTATE_BLOCKED_REASON, countAffectedPairs, formatPairCount } from './translationOverviewUtils';

type OverviewTab = 'catalogs' | 'locales';

type PendingAction =
  | { kind: 'translate' | 'refresh'; catalogIds?: Array<string>; locales?: Array<string>; pairCount: number | null }
  // The queued bulk upload. `catalogIds` undefined means "every catalog with rows waiting".
  | { kind: 'uploadPending'; catalogIds?: Array<string>; rowCount: number | null }
  // The original synchronous per-locale upload, kept unchanged.
  | { kind: 'upload'; locales: Array<string> };

const ACTION_COPY = {
  translate: {
    title: 'Translate missing values',
    confirmLabel: 'Translate',
    verb: 'Queues translation requests for every value that is not already translated',
  },
  refresh: {
    title: 'Refresh translations',
    confirmLabel: 'Refresh',
    verb: 'Re-reads the translation service and updates any value that has since completed',
  },
} as const;

function getActionDescription(action: PendingAction): string {
  if (action.kind === 'uploadPending') {
    const scope =
      action.catalogIds === undefined
        ? 'every catalog holding rows that have not been uploaded since they were translated'
        : `${action.catalogIds.length.toLocaleString()} selected catalog${action.catalogIds.length === 1 ? '' : 's'}`;

    const rows = action.rowCount === null ? '' : ` That is about ${action.rowCount.toLocaleString()} rows.`;

    return `Queues a Braze upload for ${scope}. Each locale is judged on its own, so a catalog sends the locales that are translated and leaves the rest for a later run.${rows}`;
  }

  if (action.kind === 'upload') {
    return `Uploads ${action.locales.length.toLocaleString()} locale${
      action.locales.length === 1 ? '' : 's'
    } to Braze: ${action.locales.join(', ')}.`;
  }

  const scope =
    action.pairCount === null ? 'every catalog and every locale' : `about ${formatPairCount(action.pairCount)}`;

  return `${ACTION_COPY[action.kind].verb}. This affects ${scope}.`;
}

function isUploadAction(action: PendingAction | null): boolean {
  return action?.kind === 'upload' || action?.kind === 'uploadPending';
}

export default function TranslationOverviewPage() {
  const { enqueueSnackbar } = useSnackbar();
  // The page is readable by hero-planner-viewer, but every action here writes. The API already
  // enforces this with canEditHeroPlannerMiddleware, so this only stops a viewer being offered
  // controls that can return nothing but a 403 - matching TranslationCatalogPage.
  const { user } = useCurrentUser();
  const canMutate = !!user?.roles.includes(ROLE_HERO_PLANNER_ADMIN);

  const [tab, setTab] = useState<OverviewTab>('catalogs');
  const [pendingAction, setPendingAction] = useState<PendingAction | null>(null);

  const translateMutation = useTranslateTranslationOverview();
  const refreshMutation = useRefreshTranslationOverview();
  const uploadMutation = useUploadTranslationOverviewLocale();
  const uploadPendingMutation = useUploadPendingTranslationOverview();

  const isSubmitting =
    translateMutation.isPending ||
    refreshMutation.isPending ||
    uploadMutation.isPending ||
    uploadPendingMutation.isPending;

  const requestQueuedAction = useCallback(
    (
      kind: 'translate' | 'refresh',
      scope: { catalogIds?: Array<string>; locales?: Array<string> },
      pairCount: number | null,
    ) => {
      setPendingAction({ kind, ...scope, pairCount });
    },
    [],
  );

  const runUpload = useCallback(
    async (locales: Array<string>) => {
      const failures: Array<string> = [];

      // Upload is per-locale on the API and writes to Braze, so run them one at a time rather than
      // firing a burst of writes and losing track of which locale failed.
      for (const locale of locales) {
        try {
          const results = await uploadMutation.mutateAsync({ locale });
          const failedCatalogs = results.filter((result) => !result.success);
          if (failedCatalogs.length) {
            failures.push(`${locale} (${failedCatalogs.length} catalogs)`);
          }
        } catch (error) {
          failures.push(`${locale} (${error instanceof Error ? error.message : String(error)})`);
        }
      }

      if (failures.length) {
        enqueueSnackbar(`Upload failed for ${failures.join('; ')}`, { variant: 'error' });
        return;
      }

      enqueueSnackbar(`Uploaded ${locales.length.toLocaleString()} locale${locales.length === 1 ? '' : 's'} to Braze`, {
        variant: 'success',
      });
    },
    [uploadMutation, enqueueSnackbar],
  );

  const handleConfirm = useCallback(async () => {
    if (!pendingAction) {
      return;
    }

    try {
      if (pendingAction.kind === 'uploadPending') {
        const { queuedCatalogs, queuedPairs } = await uploadPendingMutation.mutateAsync({
          catalogIds: pendingAction.catalogIds,
        });

        enqueueSnackbar(
          queuedCatalogs === 0
            ? 'Nothing to upload — every fully translated catalog is already in Braze.'
            : `Queued ${queuedCatalogs.toLocaleString()} catalog${queuedCatalogs === 1 ? '' : 's'} (${formatPairCount(
                queuedPairs,
              )}) for upload. Rows already in Braze are skipped.`,
          { variant: queuedCatalogs === 0 ? 'info' : 'success' },
        );
      } else if (pendingAction.kind === 'upload') {
        await runUpload(pendingAction.locales);
      } else {
        const mutation = pendingAction.kind === 'translate' ? translateMutation : refreshMutation;
        const { queuedPairs } = await mutation.mutateAsync({
          catalogIds: pendingAction.catalogIds,
          locales: pendingAction.locales,
        });

        enqueueSnackbar(
          `Queued ${formatPairCount(queuedPairs)} for ${pendingAction.kind}. Pairs that are already translated are skipped.`,
          { variant: 'success' },
        );
      }
    } catch (error) {
      enqueueSnackbar(error instanceof Error ? error.message : String(error), { variant: 'error' });
    } finally {
      setPendingAction(null);
    }
  }, [pendingAction, runUpload, translateMutation, refreshMutation, uploadPendingMutation, enqueueSnackbar]);

  return (
    <Container maxWidth="xl">
      <PageHeader
        title="Translation Overview"
        subtitle="Translation readiness of every Braze catalog across every supported locale"
      >
        <Stack direction="row" spacing={2}>
          <Button
            variant="contained"
            color="primary"
            onClick={() => requestQueuedAction('translate', {}, null)}
            disabled={isSubmitting || !canMutate}
            title={canMutate ? undefined : MUTATE_BLOCKED_REASON}
          >
            Translate all missing
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => setPendingAction({ kind: 'uploadPending', rowCount: null })}
            disabled={isSubmitting || !canMutate}
            title={canMutate ? undefined : MUTATE_BLOCKED_REASON}
          >
            Upload pending to Braze
          </Button>
          <Button
            variant="text"
            color="primary"
            onClick={() => requestQueuedAction('refresh', {}, null)}
            disabled={isSubmitting || !canMutate}
            title={canMutate ? undefined : MUTATE_BLOCKED_REASON}
          >
            Refresh all
          </Button>
        </Stack>
      </PageHeader>

      <Tabs value={tab} onChange={(_event, value: OverviewTab) => setTab(value)}>
        <Tab value="catalogs" label="Catalogs" />
        <Tab value="locales" label="Locales" />
      </Tabs>

      <Box mt={3}>
        {tab === 'catalogs' ? (
          <CatalogsTab
            onTranslate={(catalogIds, localesPerCatalog) =>
              requestQueuedAction('translate', { catalogIds }, countAffectedPairs(catalogIds.length, localesPerCatalog))
            }
            onRefresh={(catalogIds, localesPerCatalog) =>
              requestQueuedAction('refresh', { catalogIds }, countAffectedPairs(catalogIds.length, localesPerCatalog))
            }
            onUpload={(catalogIds, rowCount) => setPendingAction({ kind: 'uploadPending', catalogIds, rowCount })}
            canMutate={canMutate}
          />
        ) : (
          <LocalesTab
            onTranslate={(locales, catalogsPerLocale) =>
              requestQueuedAction('translate', { locales }, countAffectedPairs(locales.length, catalogsPerLocale))
            }
            onRefresh={(locales, catalogsPerLocale) =>
              requestQueuedAction('refresh', { locales }, countAffectedPairs(locales.length, catalogsPerLocale))
            }
            onUpload={(locales) => setPendingAction({ kind: 'upload', locales })}
            canMutate={canMutate}
          />
        )}
      </Box>

      <ConfirmActionDialog
        open={!!pendingAction}
        title={
          isUploadAction(pendingAction) ? 'Upload to Braze' : ACTION_COPY[pendingAction?.kind ?? 'translate'].title
        }
        description={pendingAction ? getActionDescription(pendingAction) : ''}
        confirmLabel={
          isUploadAction(pendingAction) ? 'Upload' : ACTION_COPY[pendingAction?.kind ?? 'translate'].confirmLabel
        }
        warning={
          pendingAction?.kind === 'translate' || pendingAction?.kind === 'refresh'
            ? pendingAction.pairCount === null
              ? 'This runs across the whole catalogue and can take a long time to work through.'
              : undefined
            : undefined
        }
        isSubmitting={isSubmitting}
        onConfirm={handleConfirm}
        onClose={() => setPendingAction(null)}
      />
    </Container>
  );
}
